import { useQuery } from '@tanstack/react-query'
import {
  Blocks,
  BookOpen,
  Briefcase,
  Building,
  ClipboardCheck,
  Coins,
  CreditCard,
  Factory,
  LandPlot,
  LayoutDashboard,
  LogOut,
  Settings,
  User,
  UserPlus,
} from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'

import { UserAvatar } from '@/components/common/user-avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import { ADMIN_DEFAULT_HREF } from '@/constant/navItems'
import { useAuth } from '@/contexts/auth-context'
import { hasAdminDashboardAccess, Role } from '@/enums'
import onlineTestService from '@/services/online-test.service'
import { navigateWithHardFallback } from '@/utils/route-transition.utils'

import { UserProfile } from '@/types/user.type'

interface UserAccountNavProps extends React.HTMLAttributes<HTMLDivElement> {
  user: UserProfile
  triggerClassName?: string
  avatarClassName?: string
}

export function UserAccountNav({ user, triggerClassName, avatarClassName }: UserAccountNavProps) {
  const router = useRouter()
  const { logout } = useAuth()
  const { text, lang } = useLocale()
  const { isFsdsDomain } = useBranding()

  const canAccessAdminDashboard = hasAdminDashboardAccess(user?.roles)
  const isKol = !canAccessAdminDashboard && user?.roles.includes(Role.KOL)

  const fullName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined

  const { data: myTestsResponse } = useQuery({
    queryKey: ['online-test-my-tests-count'],
    queryFn: () => onlineTestService.getMyTests(),
    staleTime: 5 * 60 * 1000,
    enabled: !canAccessAdminDashboard && !isFsdsDomain,
  })
  const pendingTestCount = myTestsResponse?.meta?.totalItems ?? 0

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'cursor-pointer rounded-full p-0.5 outline-none ring-offset-background transition-all duration-200 hover:bg-gray-100 hover:text-gray-600 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          triggerClassName
        )}
      >
        <UserAvatar
          user={user}
          className={cn(
            'size-8 shadow-sm ring-2 ring-background transition-shadow duration-200 hover:shadow-md',
            avatarClassName
          )}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="z-[10050] w-64" sideOffset={8}>
        <div className="flex items-center gap-3 px-3 py-3">
          <UserAvatar user={user} className="size-10 ring-2 ring-border/50 shadow-sm" />
          <div className="flex min-w-0 flex-col">
            {fullName && <p className="truncate text-sm font-semibold leading-tight text-foreground">{fullName}</p>}
            {user?.email && <p className="truncate text-xs text-muted-foreground">{user.email}</p>}
          </div>
        </div>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70">
            {text.SETTING_BAR.PROFILE}
          </DropdownMenuLabel>
          <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
            <Link href={`/user/${user.profileId}`}>
              <User className="size-4 text-muted-foreground" />
              <span className="text-sm">{text.SETTING_BAR.PROFILE}</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
            <Link href="/my-courses">
              <Blocks className="size-4 text-muted-foreground" />
              <span className="text-sm">{text.SETTING_BAR.MY_COURSE}</span>
            </Link>
          </DropdownMenuItem>
          {!isFsdsDomain && pendingTestCount > 0 && (
            <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
              <Link href="/online-tests">
                <ClipboardCheck className="size-4 text-muted-foreground" />
                <span className="text-sm">{text.ONLINE_TEST.NAV_PENDING_COUNT(pendingTestCount)}</span>
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
            <Link href="/setting/change-password">
              <Settings className="size-4 text-muted-foreground" />
              <span className="text-sm">{text.SETTING_BAR.CHANGE_PASSWORD}</span>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        {/* FSDS-specific Features */}
        {isFsdsDomain && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                <Link href="/survey">
                  <LandPlot className="size-4 text-muted-foreground" />
                  <span className="text-sm">{text.SETTING_BAR.SURVEY}</span>
                </Link>
              </DropdownMenuItem>
              {lang !== 'vi' && (
                <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                  <Link href="/billing">
                    <CreditCard className="size-4 text-muted-foreground" />
                    <span className="text-sm">{text.SETTING_BAR.BILLING}</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                <Link href="/my-points">
                  <Coins className="size-4 text-muted-foreground" />
                  <span className="text-sm">{text.SETTING_BAR.POINTS}</span>
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </>
        )}

        {/* Management Section */}
        {(canAccessAdminDashboard || (!canAccessAdminDashboard && isFsdsDomain) || (isKol && isFsdsDomain)) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-xs uppercase tracking-wider text-muted-foreground/70">
                {text.SETTING_BAR.MANAGEMENT}
              </DropdownMenuLabel>
              {canAccessAdminDashboard && (
                <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                  <Link
                    href={ADMIN_DEFAULT_HREF}
                    onClick={event => navigateWithHardFallback(event, ADMIN_DEFAULT_HREF, router.pathname)}
                  >
                    <LayoutDashboard className="size-4 text-muted-foreground" />
                    <span className="text-sm">{text.SETTING_BAR.ADMIN_DASHBOARD}</span>
                  </Link>
                </DropdownMenuItem>
              )}

              {!canAccessAdminDashboard && isFsdsDomain && (
                <>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                    <Link href="/dashboard/jobs">
                      <Briefcase className="size-4 text-muted-foreground" />
                      <span className="text-sm">{text.SETTING_BAR.JOBS}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                    <Link href="/dashboard/companies">
                      <Building className="size-4 text-muted-foreground" />
                      <span className="text-sm">{text.SETTING_BAR.COMPANY_MANAGEMENT}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                    <Link href="/dashboard/blogs">
                      <BookOpen className="size-4 text-muted-foreground" />
                      <span className="text-sm">{text.SETTING_BAR.BLOG_MANAGEMENT}</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}

              {isKol && isFsdsDomain && (
                <>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                    <Link href="/dashboard/voucher">
                      <Factory className="size-4 text-muted-foreground" />
                      <span className="text-sm">{text.SETTING_BAR.VOUCHER_MANAGEMENT}</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer gap-3 px-3 py-2">
                    <Link href="/referred-users">
                      <UserPlus className="size-4 text-muted-foreground" />
                      <span className="text-sm">{text.SETTING_BAR.REFERRED_USERS}</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuGroup>
          </>
        )}

        {/* Logout */}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer gap-3 px-3 py-2 text-destructive focus:bg-destructive/10 focus:text-destructive"
          onSelect={logout}
        >
          <LogOut className="size-4" />
          <span className="text-sm">{text.SETTING_BAR.LOGOUT}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
